import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryCard from '@/components/base/CategoryCard';
import { JsonLD } from '@/components/base/SchemaOrg';
import type { OrganizationSchema, WebSiteSchema, WebPageSchema } from '@/components/base/SchemaOrg';
import { categories, articles } from '@/mocks/articles';

export default function Home() {
  const { t } = useTranslation();
  const featuredArticles = articles.slice(0, 3);
  const latestArticles = articles.slice(3);
  const categoryCounts: Record<string, number> = {};
  articles.forEach((a) => { categoryCounts[a.category] = (categoryCounts[a.category] || 0) + 1; });

  const orgSchema: OrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '校花文化网',
    url: 'https://xiaohua-culture.com',
    description: t('site.description'),
    contactPoint: { '@type': 'ContactPoint', email: t('footer.email'), telephone: t('footer.phone'), contactType: 'customer service' },
  };
  const webSiteSchema: WebSiteSchema = {
    '@context': 'https://schema.org', '@type': 'WebSite', name: '校花文化网', url: 'https://xiaohua-culture.com', description: t('site.description'),
    potentialAction: { '@type': 'SearchAction', target: 'https://xiaohua-culture.com/search?q={search_term_string}', 'query-input': 'required name=search_term_string' },
  };
  const webPageSchema: WebPageSchema = {
    '@context': 'https://schema.org', '@type': 'WebPage', name: t('home.hero.title'), url: 'https://xiaohua-culture.com', description: t('site.description'), isPartOf: { '@type': 'WebSite', name: '校花文化网', url: 'https://xiaohua-culture.com' },
  };

  return (
    <>
      <SEOMeta title={t('home.hero.title')} description={t('site.description')} keywords="大学校花,校花评选,校园美女,高校校花,大学生青春,校花故事,美丽校园" canonicalUrl="https://xiaohua-culture.com" />
      <JsonLD data={orgSchema as unknown as Record<string, unknown>} />
      <JsonLD data={webSiteSchema as unknown as Record<string, unknown>} />
      <JsonLD data={webPageSchema as unknown as Record<string, unknown>} />
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20Chinese%20university%20campus%20in%20spring%20with%20cherry%20blossoms%2C%20young%20female%20students%20walking%20on%20tree-lined%20path%2C%20elegant%20traditional%20architecture%20background%2C%20warm%20golden%20sunlight%2C%20dreamy%20and%20romantic%20atmosphere%2C%20soft%20natural%20colors%2C%20editorial%20photography&width=1600&height=900&seq=hero-home-campus-belle&orientation=landscape"
            alt={t('home.hero.title')}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/35"></div>
          <div className="relative z-10 w-full max-w-3xl mx-auto text-center px-4">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 tracking-wide">
              {t('home.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-3 font-heading">
              {t('home.hero.subtitle')}
            </p>
            <p className="text-sm md:text-base text-white/75 mb-8 leading-relaxed">
              {t('home.hero.description')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/category/all" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
                {t('nav.category')} <i className="ri-arrow-right-line"></i>
              </Link>
              <Link to="/about" className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium hover:bg-white/25 transition-colors whitespace-nowrap">
                {t('nav.about')}
              </Link>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">{t('home.categories')}</h2>
              <p className="text-sm text-foreground-600">探索不同主题的校园文化内容</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {categories.map((cat) => (
                <CategoryCard key={cat.slug} {...cat} count={categoryCounts[cat.slug] || 0} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">{t('home.featured')}</h2>
                <p className="text-sm text-foreground-600">编辑精选的优质内容</p>
              </div>
              <Link to="/category/all" className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap">
                {t('home.viewAll')} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article, idx) => (
                <ArticleCard key={article.id} {...article} large={idx === 0} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">{t('home.latest')}</h2>
                <p className="text-sm text-foreground-600">最新发布的校园文化内容</p>
              </div>
              <Link to="/category/all" className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap">
                {t('home.viewAll')} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-accent-500">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">加入我们的校园文化社区</h2>
            <p className="text-white/85 text-sm md:text-base leading-relaxed mb-8">
              分享你的校园故事，记录最美的青春时光。无论是校花评选的精彩瞬间，还是校园生活的点滴感悟，我们都期待你的来稿。
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/about" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-accent-600 rounded-full text-sm font-medium hover:bg-white/90 transition-colors whitespace-nowrap">
                了解更多 <i className="ri-arrow-right-line"></i>
              </Link>
              <Link to="/category/all" className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium hover:bg-white/25 transition-colors whitespace-nowrap">
                浏览文章
              </Link>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">常见问题</h2>
              <p className="text-sm text-foreground-600">关于校花文化的常见问题解答</p>
            </div>
            <div className="max-w-3xl mx-auto grid gap-4">
              {[
                { q: '什么是校花评选？', a: '校花评选是高校校园文化活动的一种形式，旨在选拔德才兼备、全面发展的优秀女生代表，展现当代女大学生的精神风貌。现代校花评选已不仅限于外貌评判，更注重学业成绩、才艺特长、公益实践等综合素养。' },
                { q: '校花评选的标准是什么？', a: '大多数高校采用综合评分体系，包括学业成绩（约20%）、才艺特长（约25%）、公益实践（约20%）、综合素质面试（约20%）和人气投票（约15%）等维度，全方位考察选手的综合能力。' },
                { q: '校花文化有什么积极意义？', a: '校花文化丰富了大学生的校园生活，展示了当代大学生多元化的价值追求。它鼓励女生在追求外在美的同时，注重内在修养的提升，同时也为校园增添了青春活力和文化色彩。' },
                { q: '如何参与校花评选活动？', a: '各高校的校花评选通常由学生会或相关社团组织。感兴趣的同学可以关注学校官方公告或学生会发布的通知，按照报名要求提交材料。一般要求在校全日制本科生或研究生，无违纪记录，并具备一定的才艺特长。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}